import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../context/WebSocketContext";
import { WidgetInsightsWebSocketMessage, WidgetInsightsRequest } from "../types";
import { Widget } from "@/modules";
import { config } from "@/config/env";

type WidgetInsightsState =
    | { status: "idle" }
    | { status: "loading"; step: string; logs: string[] }
    | { status: "done"; insights: string; recommendations: string | null; logs: string[] }
    | { status: "error"; error: string; logs: string[] };

export const useWidgetInsightsWebSocket = () => {
    const { state, sendMessage, isReady } = useWebSocketContext();
    const [insightsState, setInsightsState] = useState<WidgetInsightsState>({ status: "idle" });
    const sessionIdRef = useRef<string | null>(null);

    const wsAction = config.WIDGET_INSIGHT_EVENT_TRIGGER;

    useEffect(() => {
        const msg = state.lastMessage as unknown as WidgetInsightsWebSocketMessage;
        if (!msg || msg.action !== wsAction) return;
        if (!sessionIdRef.current || msg.data?.sessionId !== sessionIdRef.current) return;

        const { data } = msg;

        if (data.type === "step") {
            setInsightsState((prev) => ({
                status: "loading",
                step: data.step,
                logs: prev.status !== "idle" ? (prev as any).logs ?? [] : [],
            }));
        } else if (data.type === "log") {
            setInsightsState((prev) => {
                if (prev.status === "idle") return prev;
                const logs = [...((prev as any).logs ?? []), data.message];
                return { ...prev, logs } as WidgetInsightsState;
            });
        } else if (data.type === "done") {
            setInsightsState((prev) => ({
                status: "done",
                insights: data.insights,
                recommendations: data.recommendations,
                logs: prev.status !== "idle" ? (prev as any).logs ?? [] : [],
            }));
        } else if (data.type === "error") {
            setInsightsState((prev) => ({
                status: "error",
                error: data.error,
                logs: prev.status !== "idle" ? (prev as any).logs ?? [] : [],
            }));
        }
    }, [state.lastMessage, wsAction]);

    const sendRequest = useCallback(
        (widget: Widget & { data: any }, dealerId: string, oemName?: string, visibleMetrics?: string[], productBudgets?: Record<string, string>) => {
            const sessionId = `widget-insights-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
            sessionIdRef.current = sessionId;
            setInsightsState({ status: "loading", step: "Starting...", logs: [] });

            const baseRequest: WidgetInsightsRequest = {
                action: wsAction,
                sessionId,
                dealerId,
                widget,
                ...(oemName ? { oemName } : {}),
                ...(visibleMetrics ? { visibleMetrics } : {}),
                ...(productBudgets ? { productBudgets } : {}),
            };

            const WS_SIZE_LIMIT = 100_000;
            const serialized = JSON.stringify(baseRequest);
            const request: WidgetInsightsRequest = serialized.length > WS_SIZE_LIMIT
                ? { action: wsAction, sessionId, dealerId, widget: { ...widget, data: undefined }, ...(oemName ? { oemName } : {}), ...(visibleMetrics ? { visibleMetrics } : {}), ...(productBudgets ? { productBudgets } : {}) }
                : baseRequest;

            sendMessage(JSON.stringify(request));
        },
        [sendMessage, wsAction]
    );

    const reset = useCallback(() => {
        sessionIdRef.current = null;
        setInsightsState({ status: "idle" });
    }, []);

    return {
        isReady,
        insightsState,
        sendRequest,
        reset,
    };
};
